import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { missionAlias } from '../domain/schema';
import StatusPill from './statusPill';

/**
 * MissionSwitcher — modal picker for the active mission, styled after the debugger's
 * "Select Session" dialog (workflow/generic/debugger/session/select.js): a wide modal with a
 * selectable AgGrid table and a footer bar carrying context + Cancel / Switch actions.
 *
 * Single-click selects a row; double-click OR the footer "Switch mission" button applies it (the
 * parent closes the popup via onSelect). The current mission is pre-selected when the popup opens.
 *
 * Props:
 *   show      — boolean
 *   missions  — [{ id, title, status, cellCount }] rows
 *   currentId — the active mission id (pre-selected + summarised in the footer)
 *   onHide    — () => void
 *   onSelect  — (missionId) => void  — apply the mission (caller closes the popup)
 */
export default class MissionSwitcher extends Component {
  constructor(props) {
    super(props);
    this.state = { selectedId: null };
  }

  componentDidUpdate(prevProps) {
    // Seed the selection to the current mission each time the popup opens.
    if (this.props.show && !prevProps.show) {
      this.setState({ selectedId: this.props.currentId || null });
    }
  }

  _apply(id) {
    if (id) this.props.onSelect(id);
  }

  render() {
    const { show, missions, currentId, onHide } = this.props;
    const { selectedId } = this.state;
    const rows = missions || [];
    const current = rows.find(m => m.id === currentId) || null;

    const columnDefs = [
      {
        field: 'title', headerName: 'Mission', flex: 6, filter: true,
        cellRenderer: p => p.value || <span className="text-muted">Untitled</span>,
      },
      { headerName: 'Alias', flex: 8, valueGetter: p => missionAlias(p.data.id) },
      // An `archived` mission is never re-read by the orchestrator, so anything added to one sits
      // there for ever. This is where a person chooses which mission to work in, which makes it the
      // place that has to say so.
      {
        field: 'status', headerName: 'Status', flex: 3, filter: true,
        cellRenderer: p => <StatusPill status={p.value} />,
      },
      { field: 'cellCount', headerName: 'Cells', flex: 2, type: 'numericColumn' },
    ];

    return (
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Switch Mission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted" style={{ fontSize: '0.85rem' }}>
            Pick the mission to work on. Double-click a row, or select it and choose Switch Mission.
          </p>
          <div className="ag-theme-quartz" style={{ height: 360 }}>
            <AgGridReact
              rowData={rows}
              columnDefs={columnDefs}
              rowSelection="single"
              // A stable identity, for the reason session/select.js carries one: `rows` is rebuilt
              // on every render, and without an id AG Grid drops the selection it was just given.
              getRowId={(params) => params.data.id}
              onGridReady={(e) => {
                e.api.forEachNode(n => {
                  if (n.data && n.data.id === currentId) n.setSelected(true);
                });
              }}
              onSelectionChanged={(e) => {
                const sel = e.api.getSelectedRows();
                this.setState({ selectedId: sel.length ? sel[0].id : null });
              }}
              onRowDoubleClicked={(e) => this._apply(e.data && e.data.id)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <span className="text-muted" style={{ fontSize: '0.85rem' }}>
            {rows.length} mission{rows.length === 1 ? '' : 's'}
            {current && <> &middot; current: <strong>{current.title || current.id}</strong></>}
          </span>
          <span>
            {/* A word space is not button spacing. These sit in their own span, so the footer's
                own child margins never reached them and the two read as one control. */}
            <Button variant="light" className="mr-2" onClick={onHide}>Cancel</Button>
            <Button variant="success" disabled={!selectedId} onClick={() => this._apply(selectedId)}>
              Switch Mission
            </Button>
          </span>
        </Modal.Footer>
      </Modal>
    );
  }
}
